import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Col, FormControl, FormGroup, FormLabel, Modal, Tab } from "react-bootstrap";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { usePropertyLocales } from "../../../../components/property-locale-provider";
import { useProperty } from "../../../../components/property-provider";
import { ExtraAPIPromiseClient } from "../../../../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import {
  DownloadInventoryLinesExcelRequest,
  ProcessInventoryLinesExcelRequest,
} from "../../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../../../lib/grpc";
import { createExcelFileDownloadFromRawData } from "../../../../lib/supportFunctions";
import { TranslationComponent } from "../../../shared/widgets/TranslationComponent";
import SmartSelectorList from "../../../witadmin/CRUD/Common/SmartSelectorList";
import SmartSelector from "../../../witadmin/CRUD/Common/SmartSelectorV2";
import { EditNotAllowedFormLabel } from "../../components/EditNotAllowedFormLabel";
import { ErrorFieldMessage } from "../../components/ErrorFieldMessage";
import ExcelHandlerForm from "../../components/ExcelHandlerForm";
import { InputToggle } from "../../components/InputToggle";
import { OpenBooking } from "../../components/OpenBooking";
import { RateApplyToGuestsInput } from "../../components/RateInputs/RateApplyToGuestsInput";
import { RateApplyToInput } from "../../components/RateInputs/RateApplyToInput";
import { RateAvailabilityInput } from "../../components/RateInputs/RateAvailabilityInput";
import { RateMaxUnitsInput } from "../../components/RateInputs/RateMaxUnitsInput";
import { SelectGroup } from "../../components/SelectGroup";
import { filterMessages } from "../../messages";
import { ExtraRateType, ExtraRestrictionUse, ExtraType } from "../../types";
import { useInventory } from "../hooks/inventory.hook";
import { useModal } from "../hooks/modal.hook";
import {
  EXTRA_LOCK_TYPE_MESSAGE,
  EXTRA_RATE_TYPE_MESSAGE,
  EXTRA_RESTRICTION_USE_TYPE_MESSAGE,
  EXTRA_TYPE_MESSAGE,
  EXTRA_USE_MESSAGE,
  messages,
} from "../messages";
import { Container, StyledBackButton, WitLabel } from "../styles";
import {
  CRUD_MODE,
  CustomExtra,
  ExtraModalActionType,
  FormType,
  SmartInventoryListType,
} from "../types";

interface PropertiesTabPanelProps {
  methods: UseFormReturn<FormType>;
  extra: CustomExtra;
  mode?: CRUD_MODE;
  isFromAgr: boolean;
  handlerAction: (formValue: FormType) => void;
  handleOnBack: () => void;
}

const TICKER_MAX_LENGTH = 8;

export function PropertiesTabPanel({
  methods,
  extra,
  mode,
  isFromAgr,
  handlerAction,
  handleOnBack,
}: PropertiesTabPanelProps) {
  const intl = useIntl();
  const allowedLocales = usePropertyLocales();
  const property = useProperty();
  const { showModal } = useModal();
  const {
    allowedChainInventoryLinesTickers,
    allowedChainInventoryLinesOptions,
    allowedChainInventoryLines,
    allowedInventoryLines,
    loadChainInventoryLinesAllowed,
    loadInventoryLinesAllowed,
  } = useInventory();

  const [showEditByExcelModal, setShowEditByExcelModal] = useState(false);

  const grpcClient = useGrpcClient(ExtraAPIPromiseClient);
  const downloadInventoryLinesExcel = usePropertyGrpcCall(
    grpcClient,
    grpcClient.downloadInventoryLinesExcel
  );
  const processInventoryLinesExcel = usePropertyGrpcCall(
    grpcClient,
    grpcClient.processInventoryLinesExcel
  );

  const isCreation = useMemo(() => CRUD_MODE.ADD === mode, [mode]);
  const isModification = useMemo(() => CRUD_MODE.EDIT === mode, [mode]);

  const watchType = useWatch({ control: methods.control, name: "type" });
  const watchRateType = useWatch({ control: methods.control, name: "rateType" });
  const watchMinStayType = useWatch({ control: methods.control, name: "minStayType" });
  const watchMaxStayType = useWatch({ control: methods.control, name: "maxStayType" });
  const watchMinNoticeType = useWatch({ control: methods.control, name: "minNoticeType" });
  const watchMaxNoticeType = useWatch({ control: methods.control, name: "maxNoticeType" });

  const tickerMaxLengthValidation = useMemo(() => {
    return isFromAgr || isModification ? undefined : TICKER_MAX_LENGTH;
  }, [isFromAgr, isModification]);

  const renderTranslationComponent = useMemo(
    () => (
      <TranslationComponent
        allowedLocales={allowedLocales}
        defaultValues={extra?.translationsList}
        disabled={isFromAgr}
      />
    ),
    [allowedLocales, extra?.translationsList, isFromAgr]
  );

  /**
   * Excel import/export
   */

  const handleEditByExcel = useCallback(() => setShowEditByExcelModal(true), []);

  const handleExportInventoryLinesExcel = useCallback(
    async (onFinish: () => void) => {
      try {
        const request = new DownloadInventoryLinesExcelRequest();
        request.setExtraId(extra.id);
        const response = await downloadInventoryLinesExcel(request);
        const fileData = response.getFileData_asU8();
        const fileName = `hotel-extra-updater-${property.ticker}-${Date.now()}.xlsx`;
        createExcelFileDownloadFromRawData(fileData, fileName);
        onFinish();
      } catch (e) {
        console.error(e);
        await showModal({ type: ExtraModalActionType.ERROR_GET_INVENTORY_LINES_EXCEL }, () =>
          setShowEditByExcelModal(false)
        );
      }
    },
    [downloadInventoryLinesExcel, extra.id, property, showModal]
  );

  const handleImportInventoryLinesExcel = useCallback(
    async (data: ArrayBuffer) => {
      try {
        const request = new ProcessInventoryLinesExcelRequest();
        request.setContent(new Uint8Array(data));
        const response = await processInventoryLinesExcel(request);

        const sortedChainInventoryLinesList = response
          .toObject()
          .dataMap.sort((a, b) => {
            return (
              allowedChainInventoryLinesTickers.indexOf(a[0]) -
              allowedChainInventoryLinesTickers.indexOf(b[0])
            );
          })
          .map(it => it[1].tickersList);

        setShowEditByExcelModal(false);
        methods.setValue("chainInventoryLinesList", sortedChainInventoryLinesList);
      } catch (e) {
        console.error(e);
        await showModal({ type: ExtraModalActionType.ERROR_PROCESS_INVENTORY_LINES_EXCEL }, () =>
          setShowEditByExcelModal(false)
        );
      }
    },
    [allowedChainInventoryLinesTickers, methods, processInventoryLinesExcel, showModal]
  );

  return (
    <Tab.Pane key="properties" eventKey="properties">
      {isModification && isFromAgr && (
        <EditNotAllowedFormLabel
          linkTo={`/${extra.parentPropertyTicker}/extras-edit/${extra.id}`}
        />
      )}
      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage {...messages.active} />
          </WitLabel>
        </FormLabel>
        <Col sm={2}>
          <Controller
            name="active"
            control={methods.control}
            render={({ field }) => (
              <InputToggle className="witadmin-bs3" disabled={isFromAgr} {...field} />
            )}
          />
        </Col>
      </FormGroup>
      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.extraTicker} /> {"*"}
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="ticker"
            control={methods.control}
            rules={{
              required: true,
              pattern: { value: /^[a-zA-Z0-9_]+$/, message: "" },
              maxLength: tickerMaxLengthValidation,
            }}
            render={({ field }) => (
              <FormControl
                readOnly={isFromAgr || CRUD_MODE.EDIT === mode}
                {...field}
                maxLength={TICKER_MAX_LENGTH}
              />
            )}
          />
          {methods.formState.errors.ticker?.type === "required" && (
            <ErrorFieldMessage {...messages.presenceValidator} />
          )}
          {methods.formState.errors.ticker?.type === "pattern" && (
            <ErrorFieldMessage {...messages.tickerValidation} />
          )}
          {methods.formState.errors.ticker?.type === "maxLength" && (
            <ErrorFieldMessage {...messages.maxLengthTickerValidator} />
          )}
        </Col>
      </FormGroup>

      <FormGroup as={Col}>
        <Col sm={12}>{renderTranslationComponent}</Col>
      </FormGroup>

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage {...messages.rates} />
          </WitLabel>
        </FormLabel>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.rateType} /> {"*"}
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="rateType"
            rules={{
              required: true,
              validate: type => type !== ExtraRateType.EXTRA_RATE_TYPE_UNKNOWN,
            }}
            control={methods.control}
            render={({ field }) => (
              <SelectGroup
                optionDisplay={EXTRA_RATE_TYPE_MESSAGE}
                disabled={isFromAgr}
                {...field}
              />
            )}
          />
          {methods.formState.errors.rateType && (
            <ErrorFieldMessage {...messages.presenceValidator} />
          )}
        </Col>
      </FormGroup>
      {(() => {
        switch (watchRateType) {
          case ExtraRateType.EXTRA_RATE_TYPE_CONSTANT:
            return (
              <FormGroup as={Col}>
                <Col sm={3}>
                  <Controller
                    rules={{
                      min: 0,
                    }}
                    name="ratePrice"
                    control={methods.control}
                    render={({ field }) => <FormControl disabled={isFromAgr} {...field} />}
                  />
                  {methods.formState.errors.ratePrice?.type === "min" && (
                    <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 0 }} />
                  )}
                </Col>
              </FormGroup>
            );
        }
      })()}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.type} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="type"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup optionDisplay={EXTRA_TYPE_MESSAGE} disabled={isFromAgr} {...field} />
            )}
          />
        </Col>
      </FormGroup>
      {(() => {
        switch (watchType) {
          case ExtraType.EXTRA_TYPE_ROOM_PER_DAY:
          case ExtraType.EXTRA_TYPE_PERSON_PER_DAY:
            return <RateAvailabilityInput disabled={isFromAgr} {...methods} />;
          case ExtraType.EXTRA_TYPE_ROOM_SPECIFIC_DATE:
          case ExtraType.EXTRA_TYPE_PERSON_SPECIFIC_DATE:
            return (
              <>
                <RateAvailabilityInput disabled={isFromAgr} {...methods} />
                <RateApplyToInput disabled={isFromAgr} {...methods} />
              </>
            );
          case ExtraType.EXTRA_TYPE_UNIT:
            return <RateMaxUnitsInput disabled={isFromAgr} {...methods} />;
          case ExtraType.EXTRA_TYPE_UNIT_PER_DAY:
            return (
              <>
                <RateAvailabilityInput disabled={isFromAgr} {...methods} />
                <RateMaxUnitsInput disabled={isFromAgr} {...methods} />
              </>
            );
          case ExtraType.EXTRA_TYPE_UNIT_SPECIFIC_DATE:
            return (
              <>
                <RateAvailabilityInput disabled={isFromAgr} {...methods} />
                <RateApplyToInput disabled={isFromAgr} {...methods} />
                <RateMaxUnitsInput disabled={isFromAgr} {...methods} />
              </>
            );
        }
      })()}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.blockage} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="blockage"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup optionDisplay={EXTRA_USE_MESSAGE} disabled={isFromAgr} {...field} />
            )}
          />
        </Col>
      </FormGroup>

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.lockType} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="lockType"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup
                optionDisplay={EXTRA_LOCK_TYPE_MESSAGE}
                disabled={isFromAgr}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>

      {(() => {
        switch (watchType) {
          case ExtraType.EXTRA_TYPE_PERSON:
          case ExtraType.EXTRA_TYPE_PERSON_MANDATORY:
          case ExtraType.EXTRA_TYPE_PERSON_PER_DAY:
          case ExtraType.EXTRA_TYPE_PERSON_PER_DAY_MANDATORY:
          case ExtraType.EXTRA_TYPE_PERSON_SPECIFIC_DATE:
            return <RateApplyToGuestsInput disabled={isFromAgr} {...methods} />;
        }
      })()}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage {...messages.codes} />
          </WitLabel>
        </FormLabel>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.promoId} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            rules={{
              required: false,
              pattern: /^\S*$/,
            }}
            name="codesList"
            control={methods.control}
            render={({ field }) => (
              <FormControl
                disabled={isFromAgr}
                {...field}
                value={field.value ? field.value.replace(/;/g, ",") : ""}
              />
            )}
          />
          {methods.formState.errors.codesList?.type === "pattern" && (
            <ErrorFieldMessage {...messages.emptySpacesValidator} />
          )}
        </Col>
      </FormGroup>

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage {...messages.validity} />
          </WitLabel>
        </FormLabel>
        <Col sm={3}>
          <Controller
            rules={{
              required: false,
              pattern: /^\S*$/,
            }}
            name="travelWindow"
            control={methods.control}
            render={() => (
              <OpenBooking
                disabled={isFromAgr}
                field={{ isRange: false, startField: "startDate", endField: "endDate" }}
                defaultValues={extra.travelWindow}
                onChange={data => methods.setValue("travelWindow", data)}
                intl={intl}
              />
            )}
          />
        </Col>
      </FormGroup>

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.minStay} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="minStayType"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup
                disabled={isFromAgr}
                optionDisplay={EXTRA_RESTRICTION_USE_TYPE_MESSAGE}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>
      {watchMinStayType === ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT && (
        <FormGroup as={Col}>
          <Col sm={3}>
            <Controller
              rules={{ min: 2 }}
              name="minStay"
              control={methods.control}
              render={({ field }) => <FormControl disabled={isFromAgr} {...field} />}
            />
            {methods.formState.errors.minStay?.type === "min" && (
              <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 2 }} />
            )}
          </Col>
        </FormGroup>
      )}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.maxStay} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="maxStayType"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup
                disabled={isFromAgr}
                optionDisplay={EXTRA_RESTRICTION_USE_TYPE_MESSAGE}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>
      {watchMaxStayType === ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT && (
        <FormGroup as={Col}>
          <Col sm={3}>
            <Controller
              rules={{ min: 2 }}
              name="maxStay"
              control={methods.control}
              render={({ field }) => <FormControl disabled={isFromAgr} {...field} />}
            />
            {methods.formState.errors.maxStay?.type === "min" && (
              <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 2 }} />
            )}
          </Col>
        </FormGroup>
      )}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.minNotice} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="minNoticeType"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup
                disabled={isFromAgr}
                optionDisplay={EXTRA_RESTRICTION_USE_TYPE_MESSAGE}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>
      {watchMinNoticeType === ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT && (
        <FormGroup as={Col}>
          <Col sm={3}>
            <Controller
              rules={{ min: 1 }}
              name="minNotice"
              control={methods.control}
              render={({ field }) => <FormControl disabled={isFromAgr} {...field} />}
            />
          </Col>
        </FormGroup>
      )}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.maxNotice} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="maxNoticeType"
            control={methods.control}
            render={({ field }) => (
              <SelectGroup
                disabled={isFromAgr}
                optionDisplay={EXTRA_RESTRICTION_USE_TYPE_MESSAGE}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>
      {watchMaxNoticeType === ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT && (
        <FormGroup as={Col}>
          <Col sm={3}>
            <Controller
              rules={{ min: 1 }}
              name="maxNotice"
              control={methods.control}
              render={({ field }) => <FormControl disabled={isFromAgr} {...field} />}
            />
          </Col>
        </FormGroup>
      )}

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage {...messages.emails} />
          </WitLabel>
        </FormLabel>
        <Col sm={3}>
          <FormattedMessage {...messages.emailsHelperText} />
          <Controller
            rules={{
              required: false,
              pattern: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
            }}
            name="emailsList"
            control={methods.control}
            render={({ field }) => <FormControl disabled={isFromAgr} {...field} />}
          />
          {methods.formState.errors.emailsList?.type === "pattern" && (
            <ErrorFieldMessage {...messages.validEmailValidator} />
          )}
        </Col>
      </FormGroup>

      <FormGroup as={Col}>
        <FormLabel column sm={12}>
          <WitLabel>
            <FormattedMessage {...messages.inventoryLines} />
          </WitLabel>
        </FormLabel>
        {!loadChainInventoryLinesAllowed && !loadInventoryLinesAllowed && (
          <Col sm={12}>
            <FontAwesomeIcon icon={faCircleNotch} spin />
          </Col>
        )}
        {loadChainInventoryLinesAllowed && (
          <Col sm={12}>
            <Controller
              name="chainInventoryLinesList"
              control={methods.control}
              rules={{
                required: isCreation && allowedChainInventoryLines.length > 0,
              }}
              render={({ field: { ref, ...field } }) => (
                <Container className="witadmin-bs3 witadmin-bs3-parent">
                  <SmartSelectorList
                    formValues={extra}
                    initialValues={extra.chainInventoryLinesList}
                    field={{
                      disabled: isFromAgr,
                      fieldName: "chainInventoryLinesList",
                      filterNames: [
                        intl.formatMessage(filterMessages.accommodation),
                        intl.formatMessage(filterMessages.conditions),
                        intl.formatMessage(filterMessages.ocupation),
                        intl.formatMessage(filterMessages.mealplan),
                      ],
                      defaultValue: [],
                      options: allowedChainInventoryLinesOptions,
                      propertyTickers: allowedChainInventoryLinesTickers,
                      staticFilters: {},
                      onEditByExcel: handleEditByExcel,
                    }}
                    {...field}
                    onChange={({ value }: SmartInventoryListType) => {
                      methods.setValue("chainInventoryLinesList", value);
                    }}
                  />
                </Container>
              )}
            />
          </Col>
        )}
        {loadInventoryLinesAllowed && (
          <Col sm={12}>
            <Controller
              name="inventoryLinesList"
              control={methods.control}
              render={({ field: { ref, ...field } }) => (
                <Container className="witadmin-bs3 witadmin-bs3-parent">
                  <SmartSelector
                    initialValues={extra?.inventoryLinesList}
                    field={{
                      disabled: isFromAgr,
                      fieldName: "inventoryLinesList",
                      filterNames: [
                        intl.formatMessage(filterMessages.accommodation),
                        intl.formatMessage(filterMessages.conditions),
                        intl.formatMessage(filterMessages.ocupation),
                        intl.formatMessage(filterMessages.mealplan),
                      ],
                      defaultValue: [],
                      options: allowedInventoryLines,
                      staticFilters: {},
                    }}
                    {...field}
                    onChange={(it: any) => {
                      methods.setValue("inventoryLinesList", it);
                      if (it.hasOwnProperty("value")) {
                        methods.setValue("inventoryLinesList", it.value);
                      }
                    }}
                  />
                </Container>
              )}
            />
          </Col>
        )}
        {Object.values(methods.formState.errors).length > 0 && (
          <ErrorFieldMessage {...messages.propertiesTabValidator} />
        )}
      </FormGroup>

      <FormGroup as={Col}>
        <Col sm={3}>
          <Button
            variant="primary"
            className="mt-3 mr-3"
            disabled={isFromAgr}
            onClick={methods.handleSubmit(handlerAction)}>
            <FormattedMessage {...messages.save} />
          </Button>
          <StyledBackButton className="mt-3" onClick={handleOnBack}>
            {intl.formatMessage(messages.goBack)}
          </StyledBackButton>
        </Col>
      </FormGroup>
      <Modal show={showEditByExcelModal} onHide={() => setShowEditByExcelModal(false)} size="xl">
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <ExcelHandlerForm
            isDisabled={isFromAgr}
            onExport={handleExportInventoryLinesExcel}
            onImport={handleImportInventoryLinesExcel}
          />
        </Modal.Body>
      </Modal>
    </Tab.Pane>
  );
}
